import React, { useState } from 'react'
import { apiService } from '../services/apiService'
import { formatCurrency } from '../utils/formatters'
import {
  DocumentArrowDownIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  TruckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const ReportsPanel = () => {
  const [loading, setLoading] = useState({})
  const [modalOpen, setModalOpen] = useState(false)
  const [modalData, setModalData] = useState(null)
  const [modalType, setModalType] = useState(null)

  const currentMonth = new Date().getMonth() + 1
  const currentYear = new Date().getFullYear()

  const setReportLoading = (reportType, isLoading) => {
    setLoading(prev => ({ ...prev, [reportType]: isLoading }))
  }

  const handleDownloadMonthlyReport = async (format) => {
    try {
      setReportLoading('monthly', true)
      await apiService.exportMonthlyReport(format, currentMonth, currentYear)
      toast.success(`Relatório mensal baixado em ${format.toUpperCase()}!`)
    } catch (error) {
      console.error('Erro ao baixar relatório:', error)
      toast.error('Erro ao baixar relatório')
    } finally {
      setReportLoading('monthly', false)
    }
  }

  const handleViewReport = async (reportType) => {
    try {
      setReportLoading(reportType, true)
      let data

      switch (reportType) {
        case 'monthly-performance':
          data = await apiService.getMonthlyPerformance(currentMonth, currentYear)
          break
        case 'dre-detailed':
          data = await apiService.getDREDetailed(currentMonth, currentYear)
          break
        case 'client-analysis':
          data = await apiService.getClientAnalysis()
          break
        case 'fleet-efficiency':
          data = await apiService.getFleetEfficiency()
          break
        default:
          throw new Error('Tipo de relatório inválido')
      }

      // Open modal with data instead of console.log
      setModalData(data)
      setModalType(reportType)
      setModalOpen(true)
      toast.success('Relatório carregado com sucesso!')
    } catch (error) {
      console.error('Erro ao carregar relatório:', error)
      toast.error('Erro ao carregar relatório')
    } finally {
      setReportLoading(reportType, false)
    }
  }

  const reports = [
    {
      id: 'monthly-performance',
      title: 'Performance Mensal',
      description: 'Relatório completo de KPIs e performance do mês',
      icon: ChartBarIcon,
      color: 'blue'
    },
    {
      id: 'dre-detailed',
      title: 'DRE Detalhado',
      description: 'Demonstrativo de Resultado do Exercício',
      icon: CurrencyDollarIcon,
      color: 'green'
    },
    {
      id: 'client-analysis',
      title: 'Análise de Clientes',
      description: 'Segmentação e análise de retenção de clientes',
      icon: UsersIcon,
      color: 'purple'
    },
    {
      id: 'fleet-efficiency',
      title: 'Eficiência da Frota',
      description: 'Performance e utilização dos veículos',
      icon: TruckIcon,
      color: 'orange'
    }
  ]

  const getColorClasses = (color) => {
    const colors = {
      blue: 'border-blue-200 bg-blue-50 text-blue-600',
      green: 'border-green-200 bg-green-50 text-green-600',
      purple: 'border-purple-200 bg-purple-50 text-purple-600',
      orange: 'border-orange-200 bg-orange-50 text-orange-600'
    }
    return colors[color] || colors.blue
  }

  const renderModalContent = () => {
    if (!modalData) return null

    const report = reports.find(r => r.id === modalType)
    const Icon = report?.icon || ChartBarIcon

    switch (modalType) {
      case 'monthly-performance':
        return renderMonthlyPerformance()
      case 'dre-detailed':
        return renderDREDetailed()
      case 'client-analysis':
        return renderClientAnalysis()
      case 'fleet-efficiency':
        return renderFleetEfficiency()
      default:
        return <pre className="text-sm overflow-auto">{JSON.stringify(modalData, null, 2)}</pre>
    }
  }

  const renderMonthlyPerformance = () => {
    const data = modalData?.data || {}

    return (
      <div className="space-y-6">
        {/* KPIs Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-xs text-blue-600 font-medium uppercase">Total de Contratos</div>
            <div className="text-3xl font-bold text-blue-900 mt-2">{data.total_contracts || 0}</div>
            <div className="text-xs text-blue-600 mt-1">Contratos ativos no período</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-xs text-green-600 font-medium uppercase">Receita Total</div>
            <div className="text-3xl font-bold text-green-900 mt-2">
              R$ {(data.total_revenue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-green-600 mt-1">Faturamento do mês</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-xs text-purple-600 font-medium uppercase">Taxa de Utilização</div>
            <div className="text-3xl font-bold text-purple-900 mt-2">
              {(data.utilization_rate || 0).toFixed(1)}%
            </div>
            <div className="text-xs text-purple-600 mt-1">Ocupação da frota</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div className="text-xs text-orange-600 font-medium uppercase">Ticket Médio</div>
            <div className="text-3xl font-bold text-orange-900 mt-2">
              R$ {(data.average_contract_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-orange-600 mt-1">Valor médio por contrato</div>
          </div>
        </div>

        {/* Revenue by Day - COMPLETE TABLE */}
        {data.revenue_by_day && data.revenue_by_day.length > 0 && (
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-gray-900 text-lg">📅 Receita Diária Completa</h4>
              <span className="text-sm text-gray-600">{data.revenue_by_day.length} dias</span>
            </div>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Receita</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">% do Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.revenue_by_day.map((item, index) => {
                    const percentage = data.total_revenue > 0 ? (Number(item.revenue) / data.total_revenue * 100) : 0
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {new Date(item.day).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                          R$ {Number(item.revenue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                          {percentage.toFixed(2)}%
                        </td>
                      </tr>
                    )
                  })}
                  <tr className="bg-gray-100 font-bold">
                    <td className="px-6 py-3 text-sm text-gray-900">TOTAL</td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">
                      R$ {(data.total_revenue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">100.00%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Additional Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 border rounded-lg p-4">
            <h5 className="font-semibold text-gray-900 mb-3">📊 Análise de Contratos</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Total de contratos:</span>
                <span className="font-semibold">{data.total_contracts || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Valor médio:</span>
                <span className="font-semibold">R$ {(data.average_contract_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Maior contrato:</span>
                <span className="font-semibold">R$ {(data.max_contract_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Menor contrato:</span>
                <span className="font-semibold">R$ {(data.min_contract_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border rounded-lg p-4">
            <h5 className="font-semibold text-gray-900 mb-3">🚗 Análise de Frota</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Taxa de utilização:</span>
                <span className="font-semibold">{(data.utilization_rate || 0).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Veículos locados:</span>
                <span className="font-semibold">{data.rented_vehicles || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Veículos disponíveis:</span>
                <span className="font-semibold">{data.available_vehicles || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total da frota:</span>
                <span className="font-semibold">{data.total_vehicles || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Card */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 rounded-lg p-6">
          <h5 className="font-bold text-lg text-gray-900 mb-4">📋 Resumo Executivo</h5>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-gray-600 uppercase font-medium">Faturamento</div>
              <div className="text-2xl font-bold text-green-700">
                R$ {(data.total_revenue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-600 uppercase font-medium">Contratos Ativos</div>
              <div className="text-2xl font-bold text-blue-700">{data.total_contracts || 0}</div>
            </div>
            <div>
              <div className="text-xs text-gray-600 uppercase font-medium">Ocupação</div>
              <div className="text-2xl font-bold text-purple-700">{(data.utilization_rate || 0).toFixed(1)}%</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderDREDetailed = () => {
    const data = modalData?.data || {}
    const margem = data.total_receitas > 0 ? ((data.resultado || 0) / data.total_receitas * 100) : 0

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
            <div className="text-xs text-green-600 font-medium uppercase">Receitas Totais</div>
            <div className="text-3xl font-bold text-green-900 mt-2">
              R$ {(data.total_receitas || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-green-600 mt-1">Faturamento bruto</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg border-2 border-red-200">
            <div className="text-xs text-red-600 font-medium uppercase">Despesas Totais</div>
            <div className="text-3xl font-bold text-red-900 mt-2">
              R$ {(data.total_despesas || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-red-600 mt-1">Custos e despesas</div>
          </div>
          <div className={`${data.resultado >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'} p-4 rounded-lg border-2`}>
            <div className={`text-xs font-medium uppercase ${data.resultado >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
              Resultado (Lucro/Prejuízo)
            </div>
            <div className={`text-3xl font-bold mt-2 ${data.resultado >= 0 ? 'text-blue-900' : 'text-red-900'}`}>
              R$ {(data.resultado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <div className={`text-xs mt-1 ${data.resultado >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
              Margem: {margem.toFixed(2)}%
            </div>
          </div>
        </div>

        {/* DRE Structure - Professional Layout */}
        <div className="bg-white border-2 border-gray-300 rounded-lg overflow-hidden">
          <div className="bg-gray-800 text-white px-6 py-4">
            <h3 className="font-bold text-lg">DEMONSTRATIVO DE RESULTADO DO EXERCÍCIO (DRE)</h3>
            <p className="text-sm text-gray-300">Período: {currentMonth}/{currentYear}</p>
          </div>

          <div className="divide-y divide-gray-200">
            {/* Receitas Section */}
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-bold text-lg text-green-700">RECEITAS</h4>
                <span className="font-bold text-lg text-green-700">
                  R$ {(data.total_receitas || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>

              {data.receitas && data.receitas.length > 0 ? (
                <div className="space-y-2 ml-4">
                  {data.receitas.map((item, index) => {
                    const percentage = data.total_receitas > 0 ? (Number(item.valor) / data.total_receitas * 100) : 0
                    return (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm text-gray-700">{item.classe}</span>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-green-600">
                            R$ {Number(item.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                          <span className="text-xs text-gray-500 ml-3">({percentage.toFixed(1)}%)</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 ml-4">Nenhuma receita registrada</p>
              )}
            </div>

            {/* Despesas Section */}
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-bold text-lg text-red-700">DESPESAS</h4>
                <span className="font-bold text-lg text-red-700">
                  (R$ {(data.total_despesas || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })})
                </span>
              </div>

              {data.despesas && data.despesas.length > 0 ? (
                <div className="space-y-2 ml-4">
                  {data.despesas.map((item, index) => {
                    const percentage = data.total_despesas > 0 ? (Number(item.valor) / data.total_despesas * 100) : 0
                    return (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm text-gray-700">{item.classe}</span>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-red-600">
                            R$ {Number(item.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                          <span className="text-xs text-gray-500 ml-3">({percentage.toFixed(1)}%)</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 ml-4">Nenhuma despesa registrada</p>
              )}
            </div>

            {/* Resultado Final */}
            <div className={`p-6 ${data.resultado >= 0 ? 'bg-blue-50' : 'bg-red-50'}`}>
              <div className="flex justify-between items-center">
                <h4 className={`font-bold text-xl ${data.resultado >= 0 ? 'text-blue-900' : 'text-red-900'}`}>
                  {data.resultado >= 0 ? 'LUCRO LÍQUIDO' : 'PREJUÍZO'}
                </h4>
                <div className="text-right">
                  <span className={`font-bold text-2xl ${data.resultado >= 0 ? 'text-blue-900' : 'text-red-900'}`}>
                    R$ {Math.abs(data.resultado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                  <div className="text-sm text-gray-600 mt-1">
                    Margem: {margem.toFixed(2)}% sobre receita
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Análises Adicionais */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
            <h5 className="font-semibold text-green-900 mb-3">💰 Composição de Receitas</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-green-700">Total de Receitas:</span>
                <span className="font-bold text-green-900">R$ {(data.total_receitas || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">Número de Fontes:</span>
                <span className="font-bold text-green-900">{data.receitas?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">Receita Média por Fonte:</span>
                <span className="font-bold text-green-900">
                  R$ {data.receitas?.length ? ((data.total_receitas || 0) / data.receitas.length).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '0,00'}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-lg p-4">
            <h5 className="font-semibold text-red-900 mb-3">📉 Composição de Despesas</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-red-700">Total de Despesas:</span>
                <span className="font-bold text-red-900">R$ {(data.total_despesas || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-700">Número de Categorias:</span>
                <span className="font-bold text-red-900">{data.despesas?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-700">% sobre Receita:</span>
                <span className="font-bold text-red-900">
                  {data.total_receitas > 0 ? ((data.total_despesas / data.total_receitas) * 100).toFixed(2) : '0.00'}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderClientAnalysis = () => {
    const data = modalData?.data || {}

    return (
      <div className="space-y-6">
        {/* Enhanced KPI Cards with borders and descriptions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-xs text-blue-600 font-medium uppercase">Total de Clientes</div>
            <div className="text-3xl font-bold text-blue-900 mt-2">{data.total_clients || 0}</div>
            <div className="text-xs text-blue-600 mt-1">Base total de clientes</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-xs text-green-600 font-medium uppercase">Clientes Ativos</div>
            <div className="text-3xl font-bold text-green-900 mt-2">{data.active_clients || 0}</div>
            <div className="text-xs text-green-600 mt-1">Com contratos vigentes</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-xs text-purple-600 font-medium uppercase">LTV Médio</div>
            <div className="text-3xl font-bold text-purple-900 mt-2">
              R$ {(data.average_lifetime_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-purple-600 mt-1">Lifetime Value médio</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div className="text-xs text-orange-600 font-medium uppercase">Taxa de Retenção</div>
            <div className="text-3xl font-bold text-orange-900 mt-2">
              {(data.retention_rate || 0).toFixed(1)}%
            </div>
            <div className="text-xs text-orange-600 mt-1">Clientes que renovam</div>
          </div>
        </div>

        {/* Professional Segmentation Table - ERP Style */}
        {data.classification && data.classification.length > 0 && (
          <div className="bg-white border-2 border-gray-300 rounded-lg overflow-hidden">
            <div className="bg-gray-800 text-white px-6 py-4">
              <h3 className="font-bold text-lg">SEGMENTAÇÃO DE CLIENTES POR TIER</h3>
              <p className="text-sm text-gray-300">Classificação baseada em receita total gerada</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Quantidade</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Receita Total</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">% da Receita</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">LTV Médio</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.classification.map((item, index) => {
                    const avgLTV = item.client_count > 0 ? Number(item.total_revenue) / item.client_count : 0
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            item.tier === 'Premium' ? 'bg-purple-100 text-purple-800' :
                            item.tier === 'Gold' ? 'bg-yellow-100 text-yellow-800' :
                            item.tier === 'Silver' ? 'bg-gray-100 text-gray-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {item.tier}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                          {item.client_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                          R$ {Number(item.total_revenue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {Number(item.revenue_percentage).toFixed(1)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                          R$ {avgLTV.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Complete Clients Table - ALL CLIENTS with scroll */}
        {data.top_clients && data.top_clients.length > 0 && (
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-gray-900 text-lg">👥 Ranking Completo de Clientes</h4>
              <span className="text-sm text-gray-600">{data.top_clients.length} clientes</span>
            </div>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Contratos</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Receita Total (LTV)</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">% do Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.top_clients.map((client, index) => {
                    const totalRevenue = data.top_clients.reduce((sum, c) => sum + Number(c.lifetime_value), 0)
                    const percentage = totalRevenue > 0 ? (Number(client.lifetime_value) / totalRevenue * 100) : 0
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                          {index + 1}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {client.client_name}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {client.total_contracts}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                          R$ {Number(client.lifetime_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                          {percentage.toFixed(2)}%
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Additional Analysis Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
            <h5 className="font-semibold text-purple-900 mb-3">💎 Análise de Valor</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-purple-700">LTV Médio Total:</span>
                <span className="font-bold text-purple-900">
                  R$ {(data.average_lifetime_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-purple-700">Clientes Premium:</span>
                <span className="font-bold text-purple-900">
                  {data.classification?.find(c => c.tier === 'Premium')?.client_count || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-purple-700">% Receita Premium:</span>
                <span className="font-bold text-purple-900">
                  {data.classification?.find(c => c.tier === 'Premium')?.revenue_percentage?.toFixed(1) || 0}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-purple-700">Receita TOP 10:</span>
                <span className="font-bold text-purple-900">
                  R$ {(data.top_clients?.slice(0, 10).reduce((sum, c) => sum + Number(c.lifetime_value), 0) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4">
            <h5 className="font-semibold text-orange-900 mb-3">📊 Métricas de Retenção</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-orange-700">Taxa de Retenção:</span>
                <span className="font-bold text-orange-900">
                  {(data.retention_rate || 0).toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-700">Clientes Ativos:</span>
                <span className="font-bold text-orange-900">{data.active_clients || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-700">Clientes Inativos:</span>
                <span className="font-bold text-orange-900">
                  {(data.total_clients || 0) - (data.active_clients || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-700">% Ativação:</span>
                <span className="font-bold text-orange-900">
                  {data.total_clients > 0 ? ((data.active_clients / data.total_clients) * 100).toFixed(1) : 0}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Executive Summary */}
        <div className="bg-gradient-to-r from-purple-50 to-orange-50 border-2 border-purple-200 rounded-lg p-6">
          <h5 className="font-bold text-lg text-gray-900 mb-4">📋 Resumo Executivo - Carteira de Clientes</h5>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div className="text-xs text-gray-600 uppercase font-medium">Base Total</div>
              <div className="text-2xl font-bold text-purple-700">{data.total_clients || 0}</div>
              <div className="text-xs text-gray-600 mt-1">Clientes cadastrados</div>
            </div>
            <div>
              <div className="text-xs text-gray-600 uppercase font-medium">LTV Médio</div>
              <div className="text-2xl font-bold text-green-700">
                R$ {(data.average_lifetime_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-gray-600 mt-1">Por cliente</div>
            </div>
            <div>
              <div className="text-xs text-gray-600 uppercase font-medium">Taxa de Retenção</div>
              <div className="text-2xl font-bold text-orange-700">
                {(data.retention_rate || 0).toFixed(1)}%
              </div>
              <div className="text-xs text-gray-600 mt-1">Renovação de contratos</div>
            </div>
            <div>
              <div className="text-xs text-gray-600 uppercase font-medium">Clientes Premium</div>
              <div className="text-2xl font-bold text-blue-700">
                {data.classification?.find(c => c.tier === 'Premium')?.client_count || 0}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                {data.classification?.find(c => c.tier === 'Premium')?.revenue_percentage?.toFixed(1) || 0}% da receita
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderFleetEfficiency = () => {
    const data = modalData?.data || {}

    return (
      <div className="space-y-6">
        {/* Enhanced KPI Cards with borders and descriptions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-xs text-blue-600 font-medium uppercase">Total de Veículos</div>
            <div className="text-3xl font-bold text-blue-900 mt-2">{data.total_vehicles || 0}</div>
            <div className="text-xs text-blue-600 mt-1">Frota completa</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-xs text-green-600 font-medium uppercase">Veículos Locados</div>
            <div className="text-3xl font-bold text-green-900 mt-2">{data.rented_vehicles || 0}</div>
            <div className="text-xs text-green-600 mt-1">Em operação ativa</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div className="text-xs text-orange-600 font-medium uppercase">Taxa de Utilização</div>
            <div className="text-3xl font-bold text-orange-900 mt-2">
              {(data.utilization_rate || 0).toFixed(1)}%
            </div>
            <div className="text-xs text-orange-600 mt-1">Ocupação da frota</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-xs text-purple-600 font-medium uppercase">Receita Média/Veículo</div>
            <div className="text-3xl font-bold text-purple-900 mt-2">
              R$ {(data.avg_revenue_per_vehicle || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-purple-600 mt-1">Performance média</div>
          </div>
        </div>

        {/* Complete Vehicle Performance Table - ALL VEHICLES with scroll */}
        {data.vehicle_performance && data.vehicle_performance.length > 0 && (
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-gray-900 text-lg">🚗 Performance Completa da Frota</h4>
              <span className="text-sm text-gray-600">{data.vehicle_performance.length} veículos</span>
            </div>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Veículo</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Contratos</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Dias Locados</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Receita Total</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Receita/Dia</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">% da Receita</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.vehicle_performance.map((vehicle, index) => {
                    const totalRevenue = data.vehicle_performance.reduce((sum, v) => sum + Number(v.total_revenue), 0)
                    const percentage = totalRevenue > 0 ? (Number(vehicle.total_revenue) / totalRevenue * 100) : 0
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                          {index + 1}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {vehicle.vehicle_name || 'N/A'}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {vehicle.contract_count}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {vehicle.total_days}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                          R$ {Number(vehicle.total_revenue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          R$ {Number(vehicle.revenue_per_day).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                          {percentage.toFixed(2)}%
                        </td>
                      </tr>
                    )
                  })}
                  {/* TOTAL ROW */}
                  <tr className="bg-gray-100 font-bold">
                    <td className="px-6 py-3 text-sm text-gray-900" colSpan="2">TOTAL</td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">
                      {data.vehicle_performance.reduce((sum, v) => sum + (v.contract_count || 0), 0)}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">
                      {data.vehicle_performance.reduce((sum, v) => sum + (v.total_days || 0), 0)}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">
                      R$ {data.vehicle_performance.reduce((sum, v) => sum + Number(v.total_revenue || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">
                      {/* Average revenue per day across all */}
                      R$ {(data.vehicle_performance.reduce((sum, v) => sum + Number(v.total_revenue || 0), 0) /
                        Math.max(data.vehicle_performance.reduce((sum, v) => sum + (v.total_days || 0), 0), 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">100.00%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Additional Analysis Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
            <h5 className="font-semibold text-green-900 mb-3">💰 Análise de Receita</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-green-700">Receita Total da Frota:</span>
                <span className="font-bold text-green-900">
                  R$ {(data.vehicle_performance?.reduce((sum, v) => sum + Number(v.total_revenue || 0), 0) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">Receita Média/Veículo:</span>
                <span className="font-bold text-green-900">
                  R$ {(data.avg_revenue_per_vehicle || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">Receita Média/Dia:</span>
                <span className="font-bold text-green-900">
                  R$ {(data.vehicle_performance?.reduce((sum, v) => sum + Number(v.total_revenue || 0), 0) /
                    Math.max(data.vehicle_performance?.reduce((sum, v) => sum + (v.total_days || 0), 0) || 1, 1) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">TOP 5 Geradores:</span>
                <span className="font-bold text-green-900">
                  R$ {(data.vehicle_performance?.slice(0, 5).reduce((sum, v) => sum + Number(v.total_revenue || 0), 0) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4">
            <h5 className="font-semibold text-orange-900 mb-3">📊 Análise de Utilização</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-orange-700">Taxa de Utilização:</span>
                <span className="font-bold text-orange-900">
                  {(data.utilization_rate || 0).toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-700">Veículos Ativos:</span>
                <span className="font-bold text-orange-900">{data.rented_vehicles || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-700">Veículos Ociosos:</span>
                <span className="font-bold text-orange-900">
                  {(data.total_vehicles || 0) - (data.rented_vehicles || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-700">Total de Contratos:</span>
                <span className="font-bold text-orange-900">
                  {data.vehicle_performance?.reduce((sum, v) => sum + (v.contract_count || 0), 0) || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Utilization Breakdown - if available */}
        <div className="bg-white border-2 border-gray-300 rounded-lg overflow-hidden">
          <div className="bg-gray-800 text-white px-6 py-4">
            <h3 className="font-bold text-lg">ANÁLISE DE OCUPAÇÃO DA FROTA</h3>
            <p className="text-sm text-gray-300">Distribuição de veículos por status de utilização</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-sm text-green-700 font-medium mb-2">Em Operação</div>
                <div className="text-3xl font-bold text-green-900">{data.rented_vehicles || 0}</div>
                <div className="text-xs text-green-600 mt-1">
                  {data.total_vehicles > 0 ? ((data.rented_vehicles / data.total_vehicles) * 100).toFixed(1) : 0}% da frota
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="text-sm text-yellow-700 font-medium mb-2">Disponíveis</div>
                <div className="text-3xl font-bold text-yellow-900">
                  {(data.total_vehicles || 0) - (data.rented_vehicles || 0)}
                </div>
                <div className="text-xs text-yellow-600 mt-1">
                  {data.total_vehicles > 0 ? (((data.total_vehicles - data.rented_vehicles) / data.total_vehicles) * 100).toFixed(1) : 0}% da frota
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm text-blue-700 font-medium mb-2">Total da Frota</div>
                <div className="text-3xl font-bold text-blue-900">{data.total_vehicles || 0}</div>
                <div className="text-xs text-blue-600 mt-1">100% da capacidade</div>
              </div>
            </div>
          </div>
        </div>

        {/* Executive Summary */}
        <div className="bg-gradient-to-r from-orange-50 to-green-50 border-2 border-orange-200 rounded-lg p-6">
          <h5 className="font-bold text-lg text-gray-900 mb-4">📋 Resumo Executivo - Eficiência da Frota</h5>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div className="text-xs text-gray-600 uppercase font-medium">Frota Total</div>
              <div className="text-2xl font-bold text-blue-700">{data.total_vehicles || 0}</div>
              <div className="text-xs text-gray-600 mt-1">Veículos cadastrados</div>
            </div>
            <div>
              <div className="text-xs text-gray-600 uppercase font-medium">Taxa de Utilização</div>
              <div className="text-2xl font-bold text-orange-700">
                {(data.utilization_rate || 0).toFixed(1)}%
              </div>
              <div className="text-xs text-gray-600 mt-1">Ocupação média</div>
            </div>
            <div>
              <div className="text-xs text-gray-600 uppercase font-medium">Receita Total</div>
              <div className="text-2xl font-bold text-green-700">
                R$ {(data.vehicle_performance?.reduce((sum, v) => sum + Number(v.total_revenue || 0), 0) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-gray-600 mt-1">Faturamento da frota</div>
            </div>
            <div>
              <div className="text-xs text-gray-600 uppercase font-medium">Média por Veículo</div>
              <div className="text-2xl font-bold text-purple-700">
                R$ {(data.avg_revenue_per_vehicle || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-gray-600 mt-1">Performance individual</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">📊 Relatórios Reais</h2>
            <p className="text-gray-600">Relatórios funcionais com dados reais do sistema</p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => handleDownloadMonthlyReport('excel')}
              disabled={loading.monthly}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              {loading.monthly ? 'Baixando...' : 'Excel'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reports.map((report) => {
            const Icon = report.icon
            const isLoading = loading[report.id]

            return (
              <div key={report.id} className={`border rounded-lg p-4 ${getColorClasses(report.color)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Icon className="h-8 w-8" />
                    <div>
                      <h3 className="font-semibold">{report.title}</h3>
                      <p className="text-sm opacity-75">{report.description}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => handleViewReport(report.id)}
                    disabled={isLoading}
                    className="bg-white bg-opacity-50 hover:bg-opacity-75 disabled:bg-opacity-25 px-3 py-1 rounded text-sm font-medium transition-all"
                  >
                    {isLoading ? 'Carregando...' : 'Ver Dados'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">📝 Como usar:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Ver Dados:</strong> Clique para carregar dados reais da API e visualizar no modal</li>
            <li>• <strong>Excel:</strong> Baixa relatório mensal completo em formato Excel</li>
            <li>• <strong>Dados atuais:</strong> Mostra dados do mês corrente ({currentMonth}/{currentYear})</li>
            <li>• <strong>Modal:</strong> Visualização formatada com tabelas e KPIs</li>
          </ul>
        </div>
      </div>

      {/* Modal de Visualização */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                {(() => {
                  const report = reports.find(r => r.id === modalType)
                  const Icon = report?.icon || ChartBarIcon
                  return (
                    <>
                      <Icon className={`h-8 w-8 ${
                        report?.color === 'blue' ? 'text-blue-600' :
                        report?.color === 'green' ? 'text-green-600' :
                        report?.color === 'purple' ? 'text-purple-600' :
                        'text-orange-600'
                      }`} />
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">{report?.title}</h2>
                        <p className="text-sm text-gray-600">Período: {currentMonth}/{currentYear}</p>
                      </div>
                    </>
                  )
                })()}
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-6 w-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {renderModalContent()}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ReportsPanel